import VimeoPlayer from "@vimeo/player";
import YoutubePlayer from "youtube-player";
const mobileMQ = window.matchMedia("(max-width: 768px)");
declare var YT: any;

// MARK: Class - Native Video Hero
export class NativeVideoHero {
  private element: HTMLElement;
  private media: HTMLElement | null;
  private playPauseButton: HTMLButtonElement | null;
  private muteUnmuteButton: HTMLButtonElement | null;
  private muteUnmuteButtonLabel: HTMLElement | null;
  private videoIsPaused: boolean;
  private videoIsMuted: boolean = true;

  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      this.media = this.element.querySelector(".home-hero__media");
      this.playPauseButton = this.element.querySelector(
        ".home-hero__video-controls-play-pause",
      );
      this.muteUnmuteButton = this.element.querySelector(
        ".home-hero__video-controls-mute-unmute",
      );
      if (this.muteUnmuteButton) {
        this.muteUnmuteButtonLabel = this.muteUnmuteButton.querySelector(
          ".home-hero__video-controls-mute-unmute-label",
        );
      }
      this.videoIsPaused = window.matchMedia(
        "(prefers-reduced-motion)",
      ).matches;
      this.init();
    }
  }

  // MARK: Init
  private init() {
    this.handleResize();
    this.handleVideoPlayerEvents();
  }

  private handleResize() {
    const resize = () => {
      this.handleVideoPlayerResize();
    };
    mobileMQ.addEventListener("change", resize);
    resize();
  }

  // MARK: Handle Native Video Player Source
  private handleVideoPlayerResize() {
    const videoElement = this.media?.querySelector("video") as HTMLVideoElement;
    const mobileID = this.media?.dataset.mobileNativeVideoSource;
    const desktopID = this.media?.dataset.desktopNativeVideoSource;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (mobileID) {
      let mobileOrDesktopVideo = mobileMQ.matches ? mobileID : desktopID;
      if (mobileOrDesktopVideo) {
        videoElement.src = mobileOrDesktopVideo;
      }
    }

    const initiallyPlaying = () => {
      this.videoIsPaused = false;
      this.media?.classList.toggle("video-playing", !this.videoIsPaused);
    };

    const initiallyMute = () => {
      this.videoIsMuted = true;
      videoElement.setAttribute("muted", "muted");
      videoElement.muted = true;
      if (this.muteUnmuteButton) {
        this.muteUnmuteButton.classList.remove(
          "home-hero__video-controls-mute-unmute--unmuted",
        );
        if (this.muteUnmuteButtonLabel) {
          this.muteUnmuteButtonLabel.innerText = "Unmute";
        }
      }
    };

    initiallyMute();
    if (!prefersReducedMotion) {
      initiallyPlaying();
    }
  }

  // MARK: Handle Native Video Player Events
  private handleVideoPlayerEvents() {
    const videoElement = this.media?.querySelector("video") as HTMLVideoElement;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    // If reduced motion is preferred, pause the video
    if (prefersReducedMotion) {
      videoElement.pause();
      this.videoIsPaused = true;
      this.media?.classList.remove("video-playing");
    }

    // Add basic video event listeners to keep state in sync
    videoElement.addEventListener("play", () => {
      this.videoIsPaused = false;
      this.media?.classList.add("video-playing");
    });

    videoElement.addEventListener("pause", () => {
      this.videoIsPaused = true;
      this.media?.classList.remove("video-playing");
    });

    // Promise-based autoplay detection - this is to detect if for some reason autoplay has not worked (ie low power mode in ios)
    if (!prefersReducedMotion && !this.videoIsPaused) {
      const playPromise = videoElement.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Autoplay failed
          this.videoIsPaused = true;
          this.media?.classList.remove("video-playing");
        });
      }
    }

    const handlePlayPause = () => {
      this.videoIsPaused = !this.videoIsPaused;
      videoElement[this.videoIsPaused ? "pause" : "play"]();
      this.media?.classList.toggle("video-playing", !this.videoIsPaused);
    };

    const handlemuteUnmute = () => {
      this.videoIsMuted = !this.videoIsMuted;
      if (this.videoIsMuted) {
        videoElement.setAttribute("muted", "muted");
        videoElement.muted = true;
        this.muteUnmuteButton?.classList.remove(
          "home-hero__video-controls-mute-unmute--unmuted",
        );
        if (this.muteUnmuteButtonLabel) {
          this.muteUnmuteButtonLabel.innerText = "Unmute";
        }
      } else {
        videoElement.removeAttribute("muted");
        videoElement.muted = false;
        this.muteUnmuteButton?.classList.add(
          "home-hero__video-controls-mute-unmute--unmuted",
        );
        if (this.muteUnmuteButtonLabel) {
          this.muteUnmuteButtonLabel.innerText = "Mute";
        }
      }
    };

    const addPlayPauseButtonEvent = () => {
      this.playPauseButton?.addEventListener("click", handlePlayPause);
    };
    const addmuteUnmuteButtonEvent = () => {
      this.muteUnmuteButton?.addEventListener("click", handlemuteUnmute);
    };

    addPlayPauseButtonEvent();
    if (this.muteUnmuteButton) {
      addmuteUnmuteButtonEvent();
    }
  }
}

// MARK: Class - Vimeo Hero
export class VimeoHero {
  private element: HTMLElement;
  private media: HTMLElement | null;
  private vimeoPlayer: any;
  private playPauseButton: HTMLButtonElement | null;
  private muteUnmuteButton: HTMLButtonElement | null;
  private muteUnmuteButtonLabel: HTMLElement | null;
  private mobileID: string | undefined;
  private desktopID: string | undefined;
  private videoIsPaused: boolean;
  private videoIsMuted: boolean = true;

  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      this.media = this.element.querySelector(".home-hero__media");
      this.playPauseButton = this.element.querySelector(
        ".home-hero__video-controls-play-pause",
      );
      this.muteUnmuteButton = this.element.querySelector(
        ".home-hero__video-controls-mute-unmute",
      );
      if (this.muteUnmuteButton) {
        this.muteUnmuteButtonLabel = this.muteUnmuteButton.querySelector(
          ".home-hero__video-controls-mute-unmute-label",
        );
      }
      if (this.media) {
        this.mobileID = this.media.dataset.mobileVimeoId;
        this.desktopID = this.media.dataset.desktopVimeoId;
      }
      this.videoIsPaused = window.matchMedia(
        "(prefers-reduced-motion)",
      ).matches;
      this.init();
    }
  }

  private init() {
    if (!this.mobileID) {
      this.createPlayer();
    }
    this.attachPlayPauseButtonEvent();
    if (this.muteUnmuteButton) {
      this.handlemuteUnmuteButton();
    }
    this.handleResize();
  }

  private handleResize() {
    const resize = () => {
      if (this.mobileID) {
        this.updatePlayer();
      }
    };
    mobileMQ.addEventListener("change", resize);
    resize();
  }

  private updatePlayer() {
    // Destroy the existing player if it exists
    if (this.vimeoPlayer && this.media) {
      this.vimeoPlayer
        .destroy()
        .then(() => {
          this.createPlayer();
        })
        .catch((error: any) => {
          console.error(error);
        });
      if (window.matchMedia("(prefers-reduced-motion)").matches) {
        this.videoIsPaused = true;
        this.media.classList.remove("video-playing");
      }
      this.videoIsMuted = true;
      this.muteUnmuteButton?.classList.remove(
        "home-hero__video-controls-mute-unmute--unmuted",
      );
      if (this.muteUnmuteButtonLabel) {
        this.muteUnmuteButtonLabel.innerText = "Unmute";
      }
    } else {
      this.createPlayer();
    }
  }

  private createPlayer() {
    let mobileOrDesktopVideo = mobileMQ.matches
      ? this.mobileID
      : this.desktopID;

    let autoplayVideo = window.matchMedia("(prefers-reduced-motion)").matches
      ? false
      : !this.videoIsPaused;

    if (!this.media) {
      console.error("Media element is not defined.");
      return;
    }

    this.vimeoPlayer = new VimeoPlayer(this.media, {
      id: this.mobileID ? Number(mobileOrDesktopVideo) : Number(this.desktopID),
      controls: false,
      loop: true,
      muted: true,
      autoplay: autoplayVideo,
    });
    this.vimeoPlayer.on("play", () => {
      if (!this.media?.classList.contains("video-playing")) {
        this.media?.classList.add("video-playing");
      }
      this.videoIsPaused = false;
    });
    this.vimeoPlayer.on("pause", () => {
      this.media?.classList.remove("video-playing");
      this.videoIsPaused = true;
    });

    // Promise-based autoplay detection for Vimeo - this is to detect if for some reason autoplay has not worked (ie low power mode in ios)
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (!prefersReducedMotion && !this.videoIsPaused) {
      this.vimeoPlayer.ready().then(() => {
        const playPromise = this.vimeoPlayer.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Autoplay failed
            this.videoIsPaused = true;
            this.media?.classList.remove("video-playing");
          });
        }
      });
    }
  }

  private handlePlayPause() {
    this.videoIsPaused = !this.videoIsPaused;
    this.vimeoPlayer[this.videoIsPaused ? "pause" : "play"]();
  }

  private attachPlayPauseButtonEvent() {
    this.playPauseButton?.addEventListener(
      "click",
      this.handlePlayPause.bind(this),
    );
  }

  private handlemuteUnmute() {
    if (this.videoIsMuted) {
      this.vimeoPlayer.setVolume(1.0);
      this.videoIsMuted = false;
      this.muteUnmuteButton?.classList.add(
        "home-hero__video-controls-mute-unmute--unmuted",
      );
      if (this.muteUnmuteButtonLabel) {
        this.muteUnmuteButtonLabel.innerText = "Mute";
      }
    } else {
      this.vimeoPlayer.setMuted(true);
      this.videoIsMuted = true;
      this.muteUnmuteButton?.classList.remove(
        "home-hero__video-controls-mute-unmute--unmuted",
      );
      if (this.muteUnmuteButtonLabel) {
        this.muteUnmuteButtonLabel.innerText = "Unmute";
      }
    }
  }

  private handlemuteUnmuteButton() {
    this.muteUnmuteButton?.addEventListener(
      "click",
      this.handlemuteUnmute.bind(this),
    );
  }
}

// MARK: Class - YouTube Hero
export class YouTubeHero {
  private element: HTMLElement;
  private media: HTMLElement | null;
  private youTubeHolder: HTMLElement | null;
  private youTubePlayer: any;
  private playPauseButton: HTMLButtonElement | null;
  private muteUnmuteButton: HTMLButtonElement | null;
  private muteUnmuteButtonLabel: HTMLElement | null;
  private mobileID: string | undefined;
  private desktopID: string | undefined;
  private videoIsPaused: boolean;
  private videoIsMuted: boolean = true;

  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      this.media = this.element.querySelector(".home-hero__media");
      this.youTubeHolder = this.element.querySelector(
        ".home-hero__media-youtube-holder",
      );
      this.playPauseButton = this.element.querySelector(
        ".home-hero__video-controls-play-pause",
      );
      this.muteUnmuteButton = this.element.querySelector(
        ".home-hero__video-controls-mute-unmute",
      );
      if (this.muteUnmuteButton) {
        this.muteUnmuteButtonLabel = this.muteUnmuteButton.querySelector(
          ".home-hero__video-controls-mute-unmute-label",
        );
      }
      this.mobileID = this.media?.dataset.mobileYoutubeId;
      this.desktopID = this.media?.dataset.desktopYoutubeId;
      this.videoIsPaused = window.matchMedia(
        "(prefers-reduced-motion)",
      ).matches;
      this.init();
    }
  }

  private init() {
    if (!this.mobileID) {
      this.createPlayer();
    }
    this.attachPlayPauseButtonEvent();
    if (this.muteUnmuteButton) {
      this.handlemuteUnmuteButton();
    }
    this.handleResize();
  }

  private handleResize() {
    const resize = () => {
      if (this.mobileID) {
        this.updatePlayer();
      }
    };
    mobileMQ.addEventListener("change", resize);
    resize();
  }

  private updatePlayer() {
    if (this.youTubePlayer) {
      this.youTubePlayer
        .destroy()
        .then(() => {
          this.createPlayer();
        })
        .catch((error: any) => {
          console.error(error);
        });
      if (window.matchMedia("(prefers-reduced-motion)").matches) {
        this.videoIsPaused = true;
        this.media?.classList.remove("video-playing");
      }
      this.videoIsMuted = true;
      this.muteUnmuteButton?.classList.remove(
        "home-hero__video-controls-mute-unmute--unmuted",
      );
      if (this.muteUnmuteButtonLabel) {
        this.muteUnmuteButtonLabel.innerText = "Unmute";
      }
    } else {
      this.createPlayer();
    }
  }

  private createPlayer() {
    let mobileOrDesktopVideo =
      mobileMQ.matches && this.mobileID ? this.mobileID : this.desktopID;
    let autoplayVideo: 0 | 1 = window.matchMedia("(prefers-reduced-motion)")
      .matches
      ? 0
      : this.videoIsPaused
        ? 0
        : 1;

    if (!this.youTubeHolder) {
      console.error("Media element is not defined.");
      return; // Exit the function if media is not defined
    }
    this.youTubePlayer = YoutubePlayer(this.youTubeHolder, {
      videoId: mobileOrDesktopVideo,
      playerVars: {
        rel: 0,
        autoplay: autoplayVideo,
        playsinline: 1,
        loop: 1,
        controls: 0,
        enablejsapi: 1,
      },
    });
    this.youTubePlayer.mute();
    if (!window.matchMedia("(prefers-reduced-motion)").matches) {
      this.youTubePlayer.playVideo();
    }

    this.youTubePlayer.on("stateChange", (event: any) => {
      if (event.data == 1) {
        if (this.media) {
          if (!this.media.classList.contains("video-playing")) {
            this.media.classList.add("video-playing");
          }
        }
        this.videoIsPaused = false;
      }
      if (event.data == YT.PlayerState.ENDED) {
        event.target.playVideo();
      }
    });

    // Promise-based autoplay detection for YouTube - this is to detect if for some reason autoplay has not worked (ie low power mode in ios)
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (!prefersReducedMotion && !this.videoIsPaused) {
      this.youTubePlayer.on("ready", () => {
        // Check after a short delay if the video actually started playing
        setTimeout(() => {
          this.youTubePlayer
            .getPlayerState()
            .then((state: number) => {
              // If not playing (state 1) and not buffering (state 3), autoplay likely failed
              if (state !== 1 && state !== 3) {
                this.videoIsPaused = true;
                this.media?.classList.remove("video-playing");
              }
            })
            .catch(() => {
              // If we can't get player state, assume autoplay failed
              this.videoIsPaused = true;
              this.media?.classList.remove("video-playing");
            });
        }, 1000);
      });
    }
  }

  private handlePlayPause() {
    this.videoIsPaused = !this.videoIsPaused;
    this.youTubePlayer[this.videoIsPaused ? "pauseVideo" : "playVideo"]();
    this.media?.classList.toggle("video-playing", !this.videoIsPaused);
  }

  private attachPlayPauseButtonEvent() {
    this.playPauseButton?.addEventListener(
      "click",
      this.handlePlayPause.bind(this),
    );
  }

  private handlemuteUnmute() {
    if (this.videoIsMuted) {
      this.youTubePlayer.unMute();
      this.videoIsMuted = false;
      this.muteUnmuteButton?.classList.add(
        "home-hero__video-controls-mute-unmute--unmuted",
      );
      if (this.muteUnmuteButtonLabel) {
        this.muteUnmuteButtonLabel.innerText = "Mute";
      }
    } else {
      this.youTubePlayer.mute();
      this.videoIsMuted = true;
      this.muteUnmuteButton?.classList.remove(
        "home-hero__video-controls-mute-unmute--unmuted",
      );
      if (this.muteUnmuteButtonLabel) {
        this.muteUnmuteButtonLabel.innerText = "Unmute";
      }
    }
  }

  private handlemuteUnmuteButton() {
    this.muteUnmuteButton?.addEventListener(
      "click",
      this.handlemuteUnmute.bind(this),
    );
  }
}

export default function homeHeroInit() {
  const nativeVideoHero = document.querySelector(
    ".home-hero[data-video-source='native']",
  ) as HTMLElement;
  const vimeoHero = document.querySelector(
    ".home-hero[data-video-source='vimeo']",
  ) as HTMLElement;
  const youTubeHero = document.querySelector(
    ".home-hero[data-video-source='youtube']",
  ) as HTMLElement;

  new NativeVideoHero(nativeVideoHero);
  new VimeoHero(vimeoHero);
  new YouTubeHero(youTubeHero);
}
